// src/components/MenuSlider.jsx
import { useRef, useState, useEffect } from "react";
import "../index.css";

export default function MenuSlider({ images = [], className = "", ariaLabel = "Menu slider" }) {
  const viewportRef = useRef(null);
  const [index, setIndex] = useState(0);

  // magnifier hooks must be before any early returns
  const [zoomOn, setZoomOn] = useState(false);
  const [lensPct, setLensPct] = useState({ x: 0.5, y: 0.5 });
  const [lensPx, setLensPx] = useState({ x: 0, y: 0 });
  const lensSize = 400;
  const zoom = 6;

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onScroll = () => {
      const i = Math.round(vp.scrollLeft / vp.clientWidth);
      setIndex((prev) => (i !== prev ? i : prev));
    };
    vp.addEventListener("scroll", onScroll, { passive: true });
    return () => vp.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const clamped = Math.max(0, Math.min(i, images.length - 1));
    vp.scrollTo({ left: clamped * vp.clientWidth, behavior: "smooth" });
    setIndex(clamped);
  };
  const prev = () => goTo(index - 1);
  const next = () => goTo(index + 1);

  const updateFromPoint = (clientX, clientY) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const rect = vp.getBoundingClientRect();
    let relX = (clientX - rect.left) / rect.width;
    let relY = (clientY - rect.top) / rect.height;
    relX = Math.max(0, Math.min(1, relX));
    relY = Math.max(0, Math.min(1, relY));
    setLensPct({ x: relX, y: relY });
    setLensPx({
      x: rect.left + relX * rect.width,
      y: rect.top + relY * rect.height,
    });
  };

  const onMouseMove = (e) => {
    if (!zoomOn) return;
    updateFromPoint(e.clientX, e.clientY);
  };
  const onTouchMove = (e) => {
    if (!zoomOn) return;
    const t = e.touches?.[0];
    if (!t) return;
    updateFromPoint(t.clientX, t.clientY);
  };
  const toggleZoom = (e) => {
    if (!zoomOn) {
      const point = e.touches?.[0] ?? e;
      updateFromPoint(point.clientX, point.clientY);
      setZoomOn(true);
    } else {
      setZoomOn(false);
    }
  };

  if (!images.length) return null;

  const currentSrc = images[Math.max(0, Math.min(index, images.length - 1))];

  return (
    <section className={`menu-slider ${className}`} aria-label={ariaLabel}>
      <button className="ms-arrow left" onClick={prev} aria-label="Previous">‹</button>

      <div
        className="ms-viewport"
        ref={viewportRef}
        aria-roledescription="carousel"
        onMouseMove={onMouseMove}
        onTouchMove={onTouchMove}
        onClick={toggleZoom}
        onTouchStart={toggleZoom}
      >
        {images.map((src, i) => (
          <figure className="ms-slide" key={i} aria-label={`Slide ${i + 1}`}>
            <img className="ms-img" src={src} alt={`Menu slide ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} />
          </figure>
        ))}

        {zoomOn && (
          <div
            className="magnifier-lens"
            style={{
              width: lensSize,
              height: lensSize,
              left: lensPx.x - lensSize / 2,
              top: lensPx.y - lensSize / 2,
              backgroundImage: `url(${currentSrc})`,
              backgroundSize: `${zoom * 100}% ${zoom * 100}%`,
              backgroundPosition: `${lensPct.x * 100}% ${lensPct.y * 100}%`,
            }}
            aria-hidden="true"
          />
        )}

        {!zoomOn && (
          <div className="magnifier-hint" aria-hidden="true">
            🔍 Click or tap to zoom
          </div>
        )}
      </div>

      <button className="ms-arrow right" onClick={next} aria-label="Next">›</button>

      <div className="ms-dots" role="tablist">
        {images.map((_, i) => (
          <button
            key={i}
            className={`ms-dot ${i === index ? "active" : ""}`}
            onClick={() => goTo(i)}
            role="tab"
            aria-selected={i === index}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
